/**
 * ReleaseTemplateModal.tsx — „Opublikuj" (publikacja szablonu dla klientów, Krok 5).
 *
 * Modal publikacji szablonu pod numerem widocznym dla klienta (X.Y):
 *   POST /admin/module-templates/:slug/release  { clientVersion, releaseNotes? }
 * Backend oznacza bieżący snapshot (aktualną wersję techniczną) jako opublikowany
 * pod tym numerem i ustawia wersję klienta szablonu.
 *
 * UWAGA: publikuje wersję ZAPISANĄ w bazie (nie lokalne edycje) — dlatego przycisk
 * wywołujący ten modal jest blokowany przy niezapisanych zmianach (patrz edytor).
 *
 * Obsługa odpowiedzi: 200 / 422 VERSION_NOT_GREATER / 409 NO_STRUCTURE / 409 NOTHING_NEW
 *   / 422 VALIDATION_FAILED / 404 TEMPLATE_NOT_FOUND.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

function extractErrorCode(err: unknown): string | undefined {
  const ax = err as AxiosError<{ error?: string }>;
  return ax.response?.data?.error;
}

export function ReleaseTemplateModal({
  open,
  onClose,
  templateSlug,
  currentClientVersion,
  onReleased,
}: {
  open: boolean;
  onClose: () => void;
  templateSlug: string;
  currentClientVersion: string;
  onReleased: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [clientVersion, setClientVersion] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');

  useEffect(() => {
    if (open) {
      setClientVersion('');
      setReleaseNotes('');
    }
  }, [open]);

  const releaseMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/admin/module-templates/${templateSlug}/release`, {
          clientVersion: clientVersion.trim(),
          releaseNotes: releaseNotes.trim() || undefined,
        })
      ).data as { clientVersion: string; technicalVersion: string },
    onSuccess: (data) => {
      toast.show({
        variant: 'success',
        description: t('moduleTemplateEditor.release.toast.success', {
          version: data.clientVersion,
        }),
      });
      onReleased();
      onClose();
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      const map: Record<string, string> = {
        VERSION_NOT_GREATER: t('moduleTemplateEditor.release.toast.versionNotGreater', {
          current: currentClientVersion,
        }),
        NO_STRUCTURE: t('moduleTemplateEditor.release.toast.noStructure'),
        NOTHING_NEW: t('moduleTemplateEditor.release.toast.nothingNew'),
        VALIDATION_FAILED: t('moduleTemplateEditor.release.toast.invalidVersion'),
        TEMPLATE_NOT_FOUND: t('moduleTemplateEditor.release.toast.templateNotFound'),
      };
      toast.show({
        variant: 'error',
        description: (code && map[code]) || t('moduleTemplateEditor.release.toast.error'),
      });
    },
  });

  const validFormat = /^\d+\.\d+$/.test(clientVersion.trim());
  const canSubmit = validFormat && !releaseMutation.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('moduleTemplateEditor.release.title')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (canSubmit) releaseMutation.mutate();
            }}
            disabled={!canSubmit}
          >
            {t('moduleTemplateEditor.release.confirm')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {t('moduleTemplateEditor.release.description', { current: currentClientVersion })}
        </p>
        <Input
          label={t('moduleTemplateEditor.release.clientVersion')}
          hint={t('moduleTemplateEditor.release.clientVersionHint')}
          value={clientVersion}
          onChange={(e) => setClientVersion(e.target.value)}
          placeholder="np. 1.2"
          data-testid="release-client-version"
        />
        <Textarea
          label={t('moduleTemplateEditor.release.notes')}
          hint={t('moduleTemplateEditor.release.notesHint')}
          value={releaseNotes}
          onChange={(e) => setReleaseNotes(e.target.value)}
          rows={3}
          data-testid="release-notes"
        />
      </div>
    </Modal>
  );
}
